import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FF, T } from "../lib/constants";
import { lessonBlocks, fmtDur } from "../lib/blocks";
import { ArrowBtn, ColorBtn, WeekNav, WeekBadge, shortLabel } from "../components/ui";
import { DiagramButton } from "../components/Diagram";
import { currentWeek, hasPlanning, mondayOfWeek, fmtRange, fmtDay } from "../lib/planning";

// Weekoverzicht (build 2.2): één week, alle groepen. Alleen lezen.
// - Telefoon/smal scherm: elke groep is een kaart; swipen tussen de kaarten of tikken
//   op een kleurknop bovenaan.
// - Laptop/breed scherm: galerij met de huidige groep groot in het midden, buren
//   half zichtbaar ernaast; doorklikken met de pijlen, kleurknoppen, pijltjestoetsen
//   of horizontaal vegen op het trackpad.
export default function WeekView({ wide, mob, plans, data, planning, week, setWeek, sel, setSel, lesTabs, setLesTab, canEdit, onEdit, renderNotes }) {
  var weekCounts = plans.map(function (p) { return data && data[p.id] ? Object.keys(data[p.id]).length : 0; });
  var maxWeek = Math.max.apply(null, [1].concat(weekCounts));
  var w = Math.min(week, maxWeek);
  var idx = Math.max(0, Math.min(sel, plans.length - 1));

  function go(i) { setSel(Math.max(0, Math.min(plans.length - 1, i))); }

  if (plans.length === 0) {
    return <p style={{ textAlign: "center", color: T.muted, marginTop: 60, fontFamily: FF }}>Er zijn nog geen seizoensplannen.</p>;
  }

  var lesKey = "lessonA"; // per groep bepaald in PlanCard (eigen Les A/B-schakelaar)

  // Seizoensplanning: datum onder het weeknummer, label "deze week"/"volgende les",
  // en een melding in een vakantieweek.
  var planned = hasPlanning(planning);
  var cur = planned ? currentWeek(planning, maxWeek) : null;
  var monday = planned ? mondayOfWeek(planning, w) : null;
  var sub = monday ? fmtRange(monday) : "van " + maxWeek;
  var badge = null;
  if (cur && cur.status && cur.status !== "after") {
    if (w === cur.week) badge = <WeekBadge kind={cur.status === "now" ? "now" : "next"} />;
    else badge = <WeekBadge kind="back" onClick={function () { setWeek(cur.week); }} />;
  }
  var notice = null;
  if (cur && w === cur.week && cur.status === "vacation") {
    notice = { title: cur.name + ", deze week geen les", text: "Hieronder de eerstvolgende les: week " + cur.week + ", vanaf maandag " + fmtDay(cur.nextMonday) + "." };
  } else if (cur && w === cur.week && cur.status === "before") {
    notice = { title: "Het seizoen is nog niet begonnen", text: "Week 1 begint op maandag " + fmtDay(cur.start) + "." };
  }

  // Eén woord per kleurknop. Komt een naam twee keer voor (twee keer "Geel"), dan
  // heet de tweede "2e geel".
  var seen = {};
  var names = plans.map(function (p) {
    var n = shortLabel(p.label);
    var k = n.toLowerCase();
    if (seen[k]) n = "2e " + k;
    seen[k] = true;
    if (!wide && /^volwassen/i.test(n)) n = "Volw.";
    return n;
  });

  var controls = (
    <div style={{ maxWidth: 1100, width: "100%", margin: "0 auto", padding: mob ? "12px 16px 10px" : "16px 24px 12px", display: "flex", flexDirection: "column", gap: 12, flex: "none" }}>
      {notice && (
        <div role="status" style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px", borderRadius: 12, background: "#FDF1E7", border: "1px solid #F3CDAE", maxWidth: 640, width: "100%", alignSelf: "center", boxSizing: "border-box" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B85A12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", marginTop: 1 }} aria-hidden="true"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#8A430D" }}>{notice.title}</div>
            <div style={{ fontSize: 13, color: "#6B4A2E", marginTop: 2 }}>{notice.text}</div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: wide ? "nowrap" : "wrap", justifyContent: "center" }}>
        <div style={{ flex: wide ? "none" : "1 1 100%", width: wide ? 380 : "auto" }}>
          <WeekNav week={w} sub={sub} badge={badge}
            onPrev={w > 1 ? function () { setWeek(w - 1); } : null}
            onNext={w < maxWeek ? function () { setWeek(w + 1); } : null} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: wide ? "nowrap" : "wrap", justifyContent: "center" }}>
        <div className="sp-noscrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", flex: wide ? "none" : "1 1 100%", minWidth: 0 }}>
          {plans.map(function (p, i) {
            return <ColorBtn key={p.id} lv={p} active={i === idx} text={names[i]} onClick={function () { go(i); }}
              style={wide ? { width: 120, flex: "none" } : { flex: "1 1 0", minWidth: 50, fontSize: 12, padding: "0 3px" }} />;
          })}
        </div>
      </div>
    </div>
  );

  var cards = plans.map(function (p, i) {
    var wk = data && data[p.id] ? data[p.id][w] : null;
    var t = (lesTabs && lesTabs[p.id]) || 0;
    return { plan: p, wk: wk, i: i, lesTab: t, setLesTab: function (v) { setLesTab(p.id, v); } };
  });

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", fontFamily: FF }}>
      {controls}
      {wide
        ? <Gallery cards={cards} idx={idx} go={go} total={plans.length} lesKey={lesKey} canEdit={canEdit} onEdit={onEdit} week={w} renderNotes={renderNotes} />
        : <Swiper cards={cards} idx={idx} go={go} total={plans.length} lesKey={lesKey} canEdit={canEdit} onEdit={onEdit} week={w} renderNotes={renderNotes} />}
    </div>
  );
}

// ---------- Telefoon: swipen ----------
function Swiper({ cards, idx, go, total, lesKey, canEdit, onEdit, week, renderNotes }) {
  var ref = useRef(null);
  var fromScroll = useRef(false);
  var scrollTimer = useRef(null);

  function step() {
    var el = ref.current;
    if (!el || !el.children[0]) return 1;
    var c = el.children[0];
    var gap = el.children[1] ? el.children[1].offsetLeft - c.offsetLeft - c.offsetWidth : 0;
    return c.offsetWidth + gap;
  }

  // Kleurknop aangeklikt -> naar die kaart schuiven.
  useEffect(function () {
    var el = ref.current;
    if (!el) return;
    if (fromScroll.current) { fromScroll.current = false; return; }
    var target = idx * step();
    if (Math.abs(el.scrollLeft - target) > 2) el.scrollTo({ left: target, behavior: "smooth" });
  }, [idx]);

  // Bij openen meteen op de juiste kaart staan, zonder animatie.
  useLayoutEffect(function () {
    var el = ref.current;
    if (el) el.scrollLeft = idx * step();
  }, []);

  function onScroll() {
    var el = ref.current;
    if (!el) return;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(function () {
      var i = Math.round(el.scrollLeft / step());
      if (i !== idx) { fromScroll.current = true; go(i); }
    }, 60);
  }

  return (
    <div ref={ref} onScroll={onScroll} className="sp-noscrollbar"
      style={{ flex: 1, minHeight: 0, display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", scrollSnapType: "x mandatory", padding: "0 16px 16px", scrollPaddingLeft: 16, WebkitOverflowScrolling: "touch" }}>
      {cards.map(function (c) {
        return (
          <div key={c.plan.id} style={{ flex: "none", width: "calc(100vw - 32px)", maxWidth: 560, scrollSnapAlign: "start", display: "flex", minHeight: 0 }}>
            <PlanCard c={c} total={total} lesKey={lesKey} canEdit={canEdit} onEdit={onEdit} week={week} renderNotes={renderNotes} />
          </div>
        );
      })}
      <div style={{ flex: "none", width: 4 }} />
    </div>
  );
}

// ---------- Laptop: galerij met doorklikken ----------
function Gallery({ cards, idx, go, total, lesKey, canEdit, onEdit, week, renderNotes }) {
  var ref = useRef(null);
  var [size, setSize] = useState({ w: 1100, h: 600 });
  var wheelAcc = useRef(0);
  var wheelLock = useRef(0);

  useLayoutEffect(function () {
    var el = ref.current;
    if (!el) return;
    function measure() { setSize({ w: el.clientWidth, h: el.clientHeight }); }
    measure();
    var ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el); else window.addEventListener("resize", measure);
    return function () { if (ro) ro.disconnect(); else window.removeEventListener("resize", measure); };
  }, []);

  // Pijltjestoetsen (niet als je in een invoerveld zit).
  useEffect(function () {
    function onKey(e) {
      var t = e.target && e.target.tagName;
      if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT") return;
      if (e.key === "ArrowLeft") go(idx - 1);
      if (e.key === "ArrowRight") go(idx + 1);
    }
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [idx, go]);

  // Horizontaal vegen op een trackpad = doorklikken.
  function onWheel(e) {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    var now = Date.now();
    if (now < wheelLock.current) return;
    wheelAcc.current += e.deltaX;
    if (Math.abs(wheelAcc.current) > 60) {
      go(idx + (wheelAcc.current > 0 ? 1 : -1));
      wheelAcc.current = 0;
      wheelLock.current = now + 450;
    }
  }

  var gap = 32;
  var cardW = Math.round(Math.max(420, Math.min(600, size.w * 0.46)));
  var cardH = Math.max(320, size.h - 24);
  var center = size.w / 2;
  var offset = center - cardW / 2 - idx * (cardW + gap);

  return (
    <div ref={ref} onWheel={onWheel} style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 4, left: 0, display: "flex", gap: gap, transform: "translateX(" + offset + "px)", transition: "transform 380ms cubic-bezier(.2,.7,.2,1)" }}>
        {cards.map(function (c) {
          var active = c.i === idx;
          return (
            <div key={c.plan.id}
              onClick={active ? undefined : function () { go(c.i); }}
              aria-hidden={!active}
              style={{ flex: "none", width: cardW, height: cardH, display: "flex", opacity: active ? 1 : 0.45, transform: "scale(" + (active ? 1 : 0.94) + ")", transition: "opacity 380ms, transform 380ms", cursor: active ? "auto" : "pointer" }}>
              <div style={{ flex: 1, display: "flex", minWidth: 0, pointerEvents: active ? "auto" : "none" }}>
                <PlanCard c={c} total={total} lesKey={lesKey} canEdit={canEdit} onEdit={onEdit} week={week} renderNotes={renderNotes} />
              </div>
            </div>
          );
        })}
      </div>
      {idx > 0 && <ArrowBtn dir="left" size={56} shadow label="Vorige groep" onClick={function () { go(idx - 1); }}
        style={{ position: "absolute", top: "50%", marginTop: -28, left: center - cardW / 2 - 28 }} />}
      {idx < total - 1 && <ArrowBtn dir="right" size={56} shadow label="Volgende groep" onClick={function () { go(idx + 1); }}
        style={{ position: "absolute", top: "50%", marginTop: -28, left: center + cardW / 2 - 28 }} />}
    </div>
  );
}

// ---------- Eén groep ----------
function PlanCard({ c, total, lesKey, canEdit, onEdit, week, renderNotes }) {
  var p = c.plan;
  var wk = c.wk;
  var les = wk ? wk[c.lesTab === 1 ? "lessonB" : "lessonA"] : null;
  return (
    <article style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: T.card, borderRadius: 18, border: "1px solid " + T.line2, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", background: p.color, color: p.on || "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flex: "none" }}>
        <h2 style={{ fontWeight: 800, fontSize: 20, margin: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</h2>
        <LesToggle value={c.lesTab} onChange={c.setLesTab} on={p.on || "#fff"} />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {!wk && <p style={{ color: T.muted, fontSize: 14, padding: "20px 4px", textAlign: "center" }}>{p.label + " heeft geen week " + week + "."}</p>}
        {wk && (
          <>
            <div style={{ padding: "2px 4px 4px" }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: wk.title ? T.ink : "#B5B0A6", lineHeight: 1.25 }}>{wk.title || "Nog niet ingevuld"}</div>
              {wk.subtitle && <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{wk.subtitle}</div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 12px", borderRadius: 12, background: p.bg || T.soft }}>
              <Field label="Thema & focus" color={p.ink || p.color} text={wk.theme} />
              <Field label="Groepsaanpassingen" color={p.ink || p.color} text={wk.adjustments} />
            </div>
            {les && lessonBlocks(les, p).map(function (et) {
              var ex = et.data;
              return (
                <div key={et.key} style={{ display: "flex", gap: 12, padding: 12, borderRadius: 12, background: et.tint || T.soft }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: "none", width: 36 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 15, background: et.bg, color: et.fg, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{et.icon}</div>
                    {ex.time != null && <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, whiteSpace: "nowrap" }}>{fmtDur(ex.time)}</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>{et.label}</div>
                      <DiagramButton diagram={ex.diagram} canEdit={false} title={et.label} />
                    </div>
                    <div aria-hidden="true" style={{ fontWeight: 700, fontSize: 15, color: "#B5B0A6", marginTop: 1 }}>—</div>
                    {ex.description && <p style={{ fontSize: 13, lineHeight: 1.5, color: "#4A4740", marginTop: 4, whiteSpace: "pre-line" }}>{ex.description}</p>}
                    {ex.accents && ex.accents.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                        {ex.accents.map(function (a, j) { return <span key={j} style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "#fff", border: "1px solid " + T.line2, color: "#4A4740", overflowWrap: "anywhere", maxWidth: "100%", boxSizing: "border-box", lineHeight: 1.35 }}>{a}</span>; })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {renderNotes && renderNotes(p, week, c.lesTab === 1 ? 1 : 0)}
            {canEdit && onEdit && (
              <button type="button" onClick={function () { onEdit(p.id, week); }} style={{ alignSelf: "flex-start", marginTop: 4, background: "none", border: 0, padding: "8px 4px", fontSize: 13, fontWeight: 700, color: p.ink || p.color, cursor: "pointer" }}>Bewerken in Seizoensoverzicht →</button>
            )}
          </>
        )}
      </div>
    </article>
  );
}

function Field({ label, color, text }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: color, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: text ? "#4A4740" : "#A39E94", marginTop: 2, whiteSpace: "pre-line" }}>{text || "—"}</p>
    </div>
  );
}

// Les A / Les B in de gekleurde titelbalk van elke kaart. Per groep: wissel je bij
// Rood naar Les B, dan blijft Groen op de les die daar openstond.
function LesToggle({ value, onChange, on }) {
  var dark = on !== "#fff" && on !== "#FFFFFF";
  return (
    <div role="tablist" aria-label="Les" style={{ display: "flex", flex: "none", gap: 2, padding: 3, borderRadius: 10, background: dark ? "rgba(0,0,0,.10)" : "rgba(255,255,255,.18)" }}>
      {[0, 1].map(function (v) {
        var act = value === v;
        return (
          <button key={v} type="button" role="tab" aria-selected={act} onClick={function () { onChange(v); }}
            style={{ height: 32, minWidth: 62, padding: "0 10px", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: FF,
              background: act ? "#fff" : "transparent", color: act ? T.ink : on, transition: "background .2s" }}>
            {v === 0 ? "Les A" : "Les B"}
          </button>
        );
      })}
    </div>
  );
}
